<script setup>
import moment from 'moment'
import 'devextreme-vue/text-area'

import { menuRoutesEnum } from '@/router'

import { computed, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxItem } from 'devextreme-vue/form'
import { DxTabPanel } from 'devextreme-vue/tab-panel'

import * as dataGrid from '@/composables/data-grid'
import { useAddressRef, updateEditorLabels, registerLabelEditors } from '@/composables/data-grid-column'
import { useColumns } from '@/composables/registry/subject-grid.js'
import { usePrint } from '@/composables/print'
import { useTaxCode } from '@/composables/tax-code'
import { useDialog } from '@/composables/dialog'
import { useAttachment } from '@/composables/registry/service-attachment-grid'

import { useNotify } from '@/composables/notify'
import { useValidationTabs } from '@/composables/validation'

import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'

import { isNullOrUndefined } from '@/lib/utils'

import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'

import AttachmentIconList from '@/components/shared/AttachmentIconList.vue'
import CustomerPrivacyModuleGrid from '@/components/registry/CustomerPrivacyModuleGrid.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import SubjectContactGrid from './SubjectContactGrid.vue'
import SubjectDocumentGrid from './SubjectDocumentGrid.vue'
import SubjectProfileGrid from './SubjectProfileGrid.vue'
import SupplierBrandTransmissionGrid from './SupplierBrandTransmissionGrid.vue'
import SupplierDivisionGrid from './SupplierDivisionGrid.vue'
import ValidationTab from '@/components/shared/ValidationTab.vue'

import { useEntityLock } from '@/services/entity-lock.service'
import { useSubjectService } from '@/services/subject.service'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const accountingStore = useAccountingStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()

const gridId = 'subject-grid'

const { gridRef, refreshGridAsync } = dataGrid.useGridRef()
const { currentCountryId, currentStateId, currentZipCode, currentFilteredCitiesCount, setCellValueZipCode } = useAddressRef()
const formInstance = ref(null)
const editors = {}
const { route, router } = dataGrid.useGridRouter()
const { notifyError } = useNotify()
const { isVatCountryIta, isVatNumberMandatory, isNameExtMandatory } = useSubjectService()
let subjectValidationType = !!route.query.subjectValidation || portalStore.subjectValidationTypesEnum.NONE
const privacyRequired = ref(!!(subjectValidationType & portalStore.subjectValidationTypesEnum.PRIVACY))
const clearSubjectValidation = () => {
  privacyRequired.value = false
  subjectValidationType = portalStore.subjectValidationTypesEnum.NONE
}
const { customDialog } = useDialog()
const print = usePrint()
const { getAttachmentEntities } = useAttachment()

defineExpose({ focusedRow: dataGrid.focusedRow })
const currentEditingTabIndex = ref(0)
const currentSubjectEntityType = ref(null)
const currentSubjectTypeId = ref(null)
const currentSubjectSubTypeId = ref(null)
const currentCertifiedEmail = ref(null)
const currentVerifyCertifiedEmail = ref(null)
const currentSdiCode = ref(null)
const currentVatCountryId = ref(null)
const currentVatCountryCode = computed(() => {
  const country = webSiteStore.countries.find(x => x.id === currentVatCountryId.value)
  return country ? country.iso3_code : null
})
const currentTradeAccountId = ref(null)
const currentFinancialCenterId = ref(null)
const currentBirthStateId = ref(null)
const currentBirthCityId = ref(null)

const { calculatedTaxCodeData, resetTaxCodeData, isTaxCodeCalculationDisabled, computeTaxCode } = useTaxCode(currentSubjectSubTypeId)

const onCalculateTaxCode = () => {
  const grid = gridRef.value?.instance
  const editingRow = grid?.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  try {
    grid.cellValue(editingRow.rowIndex, 'tax_code', computeTaxCode())
  } catch (err) {
    notifyError(i18n.t('common.taxCodeCalculationError'))
  }
}

const birthInfoGroup = ref(null)
const identityDataGroup = ref(null)
const vatNumberCell = ref(null)
const vatCountryCell = ref(null)
const taxCodeCell = ref(null)
const sdiCodeCell = ref(null)

const selectedRows = ref([])
const isViewOnly = ref(false)

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.subjectsUrl,
  modelName: 'Subject',
  gridRef,
})

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => e.entityId < 0 && !entityLock.isLocked(false, e.entityId),
    delete: (e) => e.entityId < 0 && !entityLock.isLocked(false, e.entityId),
    view: (e) => e.entityId > 0
  }
})

const getFilteredTradeAccounts = (options) => {
  return {
    store: accountingStore.getLocalizedTradeAccounts(),
    filter: (item) => {
      return accountingStore.getTradeAccountIdsByFinancialCenterId(currentFinancialCenterId.value).includes(item.id)
    },
    sort: 'code',
  }
}

const getFilteredFinancialCenters = (options) => {
  return {
    store: accountingStore.getLocalizedFinancialCenters(),
    filter: (item) => {
      return accountingStore.getFinancialCenterIdsByTradeAccountId(currentTradeAccountId.value).includes(item.id)
    },
    sort: 'code',
  }
}

const disableFields = (itemsArray) => {
  itemsArray.forEach((arrayItem) => {
    if (arrayItem) {
      if (arrayItem.itemType === 'group') {
        if (!arrayItem.items) return
        if (currentSubjectType.value) {
          arrayItem.items.forEach((item) => {
            item.editorOptions.disabled = !currentSubjectType.value.has_identity_data
          })
        }
      }
    }
  })
}

const hasIdentityData = (subjectTypeId) => {
  const subjectType = registryStore.subjectTypes.find(x => x.id === subjectTypeId)
  return subjectType?.has_identity_data
}

const updateTabPanelVisibility = () => {
  if (editingTabPanelInstance.value) {
    if (isViewOnly.value) {
      editingTabPanelInstance.value.option('items').forEach((item, index) => {
        if (index > 0) { editingTabPanelInstance.value.option(`items[${index}].visible`, false) }
      })
      return
    }
    editingTabPanelInstance.value.option('items[1].visible', (currentSubjectEntityType.value & (registryStore.subjectEntityTypes.CUSTOMER)) === registryStore.subjectEntityTypes.CUSTOMER)
    editingTabPanelInstance.value.option('items[2].visible', (currentSubjectEntityType.value & (registryStore.subjectEntityTypes.CUSTOMER)) === registryStore.subjectEntityTypes.CUSTOMER)
    editingTabPanelInstance.value.option('items[3].visible', (currentSubjectEntityType.value & (registryStore.subjectEntityTypes.CUSTOMER)) === registryStore.subjectEntityTypes.CUSTOMER)
    editingTabPanelInstance.value.option('items[4].visible', (currentSubjectEntityType.value & (registryStore.subjectEntityTypes.SUPPLIER)) === registryStore.subjectEntityTypes.SUPPLIER)
    editingTabPanelInstance.value.option('items[5].visible', (currentSubjectEntityType.value & (registryStore.subjectEntityTypes.SUPPLIER)) === registryStore.subjectEntityTypes.SUPPLIER)
  }
}

const autoEditPending = ref(route.query.openEdit === 'true')

const onGridContentReady = (e) => {
  if (!autoEditPending.value) return

  const dataSource = e.component.getDataSource()
  if (dataSource.isLoading()) return

  const rows = e.component.getVisibleRows()
  if (rows.length === 0) return

  autoEditPending.value = false
  e.component.editRow(rows[0].rowIndex)

  router.replace({ query: { ...route.query, openEdit: undefined } })
}

const onContentReady = (e) => {
  updateTabPanelVisibility()
  if (dataGrid.openEditingTab.value) {
    const navigateToTab = Number(dataGrid.openEditingTab.value)
    if (Number.isInteger(navigateToTab) && navigateToTab >= 0) {
      editingTabPanelInstance.value.option('selectedIndex', navigateToTab)
    }
  }

  if (subjectValidationType && gridRef.value?.instance) {
    gridRef.value.instance.getController('validating')?.validate(true)
    subjectValidationType = portalStore.subjectValidationTypesEnum.NONE
  }
}

const currentSubjectType = computed(() => {
  const customerTypeId = currentSubjectSubTypeId.value || currentSubjectTypeId.value
  return customerTypeId ? registryStore.getLocalizedSubjectTypes().find(x => x.id === customerTypeId) : null
})

const getSdiCodeMaxLength = () => registryStore.subjectTypes.find(subjectType => subjectType.id === currentSubjectSubTypeId.value)?.code === 'EPA' ? 6 : 7

const setLabel = (item, trueLabel, falseLabel) => {
  const legalEntityId = registryStore.subjectTypes.find(x => x.code === 'E')?.id
  currentSubjectType.value ? item.label = { text: i18n.t(currentSubjectType.value.id === legalEntityId || currentSubjectType.value.parent_id === legalEntityId ? trueLabel : falseLabel) } : null
}

const subjectLabelFields = ['name', 'name_ext', 'subject_sub_type_ref', 'vat_number', 'tax_code']
const customRequiredLabels = {
  'address.city': 'common.cityAddress',
  'customer.discount_list_ref': 'common.discountGroup',
  'customer_accounting.customer_accounting_status_type': 'common.status',
  'supplier.supplier_brand_ref': 'common.supplierBrand',
}

const buildItemLabel = (item) => {
  if (customRequiredLabels[item.dataField]) {
    item.label = { text: `${i18n.t(customRequiredLabels[item.dataField])} *` }
  }
  if (item.dataField === 'customer.privacy_check') {
    item.label = { text: `${i18n.t('common.privacyChecked')}${privacyRequired.value ? ' *' : ''}` }
  }
  if (item.dataField === 'name') {
    setLabel(item, 'common.nameAlt', 'common.lastName')
  }
  if (item.dataField === 'name_ext') {
    setLabel(item, 'common.nameExtAlt', 'common.firstName')
    if (item.label?.text && isNameExtMandatory(currentSubjectTypeId.value)) {
      item.label = { text: `${item.label.text} *` }
    }
  }
  if (item.dataField === 'subject_sub_type_ref') {
    setLabel(item, 'common.category', 'common.gender')
  }
  if (item.dataField === 'vat_number') {
    item.label = { text: isVatNumberMandatory(currentSubjectSubTypeId.value, currentVatCountryId.value) ? `${i18n.t('common.vatNumber')} *` : i18n.t('common.vatNumber') }
  }
  if (item.dataField === 'tax_code') {
    item.label = { text: isVatCountryIta(currentVatCountryId.value) ? i18n.t('common.taxCode') : i18n.t('common.fiscalId') }
  }
}

const customizeItem = (item) => {
  if (!item) return
  buildItemLabel(item)
  if (item.name === 'birthInfoGroup') birthInfoGroup.value = item
  if (item.name === 'identityDataGroup') identityDataGroup.value = item
  if (item.dataField === 'vat_country_id') vatCountryCell.value = item
  if (item.dataField === 'vat_number') vatNumberCell.value = item
  if (item.dataField === 'tax_code') taxCodeCell.value = item
  if (item.dataField === 'sdi_code') sdiCodeCell.value = item
}

const setDefaultCustomerData = (data) => {
  data.customer = {}
  data.customer.discount_list_ref = registryStore.discountLists[0]?.id
  data.customer.marketing_communications_consent = false
  data.customer.third_party_data_transfer_consent = false
  data.customer.privacy_check = false

  currentSubjectEntityType.value = data.type
  data.customer_accounting = {}
  data.customer_accounting.customer_accounting_status_type = accountingStore.customerAccountingStatusTypesEnum.ACTIVE
  data.customer_accounting.is_stamp_duty_customer = true
  data.customer_accounting.single_invoice = false
  data.customer_accounting.riba_grouping = false
  data.customer_accounting.remind_packaging = false
  data.customer_accounting.remind_insurance = false
  data.customer_accounting.remind_payment_collection = false
  data.customer_accounting.do_not_export = false

  data.customer_electronic_invoice = {}
  data.customer_electronic_invoice.split_payment = false
}

const isSupplier = (data) => {
  return !!((data?.type & (registryStore.subjectEntityTypes.SUPPLIER)) === registryStore.subjectEntityTypes.SUPPLIER)
}
const isCustomer = (data) => {
  return !!((data?.type & (registryStore.subjectEntityTypes.CUSTOMER)) === registryStore.subjectEntityTypes.CUSTOMER)
}

const columns = useColumns(
  editors,
  currentCountryId,
  currentStateId,
  currentVatCountryId,
  currentCertifiedEmail,
  currentVerifyCertifiedEmail,
  currentSubjectEntityType,
  currentSubjectType,
  currentSubjectTypeId,
  currentSubjectSubTypeId,
  currentTradeAccountId,
  currentFinancialCenterId,
  currentVatCountryCode,
  currentFilteredCitiesCount,
  getFilteredTradeAccounts,
  getFilteredFinancialCenters,
  updateTabPanelVisibility,
  setDefaultCustomerData,
  setCellValueZipCode,
  isCustomer,
  isSupplier,
  privacyRequired,
)

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const editingTabPanelInstance = ref(null)

const onTabSelectionChanged = (e) => {
  currentEditingTabIndex.value = e.component.option('selectedIndex')
}

const onInitializedCustom = (e) => {
  formInstance.value?.option('readOnly', isViewOnly.value)
  if (isViewOnly.value) {
    const toolbarItems = e.component.option('toolbarItems') || []
    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return { ...item, visible: false }
    })
    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    entityLock.readOnlyToolbarItems(e)
  }
}

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.subject')), {
  colCount: 1,
  items: [
    {
      itemType: 'tabbed',
      name: 'tabbed',
      tabPanelOptions: {
        onInitialized: (e) => { editingTabPanelInstance.value = e.component },
        onContentReady: onContentReady,
        onSelectionChanged: onTabSelectionChanged,
        deferRendering: false, // in order to get all the validation errors (even from hidden tabs)
      },
      tabs: [
        {
          title: i18n.t('common.main'),
          colCount: 2,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 0,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.mainInfo'),
              name: 'mdi-account-circle',
              captionTemplate: 'main-info-caption-template',
              colCount: 8,
              items: [
                { dataField: 'code', editorOptions: {...dataGrid.useCodeFieldEditorOptions(), tabIndex: 0 }, colSpan: 2 },
                { dataField: 'type', editorOptions: { tabIndex: 0 }, colSpan: 2 },
                { dataField: 'subject_type_ref', editorOptions: { tabIndex: 0 }, colSpan: 2 },
                { dataField: 'subject_sub_type_ref', editorOptions: { tabIndex: 0 }, colSpan: 2},
                { dataField: 'name', editorOptions: { tabIndex: 0 }, colSpan: 4 },
                { dataField: 'name_ext', editorOptions: { tabIndex: 0 }, colSpan: 4 },
                { dataField: 'vat_country_id', editorOptions: { tabIndex: 0 }, colSpan: 2},
                { dataField: 'vat_number', editorOptions: { tabIndex: 0 }, colSpan: 2},
                { dataField: 'tax_code', editorOptions: { tabIndex: 0 }, colSpan: 4},
              ]
            },
            {
              name: 'mdi-home',
              captionTemplate: 'generic-caption-template',
              ...dataGrid.useDefaultAddressEditing('address', 1, 2, { tabIndex: 0 }),
            },
            {
              itemType: 'group',
              caption: i18n.t('common.contactInfo'),
              name: 'mdi-notebook',
              captionTemplate: 'generic-caption-template',
              colCount: 2,
              items: [
                { dataField: 'mobile', editorOptions: { tabIndex: 0, onContentReady: (e) => { dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules }) }}},
                { dataField: 'phone', editorOptions: { tabIndex: 0, onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
                { dataField: 'phone_ext', editorOptions: { tabIndex: 0, onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}},
                { dataField: 'email', editorOptions: { tabIndex: 0 } },
                { dataField: 'email_ext', editorOptions: { tabIndex: 0 } },
                { dataField: 'email_certified', editorOptions: { tabIndex: 0 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.additionalInfo', 2),
              name: 'mdi-notebook-edit',
              captionTemplate: 'generic-caption-template',
              colSpan: 2,
              items: [
                {
                  itemType: 'formItem',
                  editorType: 'dxTextArea',
                  dataField: 'notes',
                  editorOptions: { autoResizeEnabled: true, height: '4.5rem', tabIndex: 0 }
                },
              ]
            }
          ]
        },
        {
          title: i18n.t('common.customer'),
          colCount: 2,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 1,
          items: [
            {
              itemType: 'group',
              name: 'birthInfoGroup',
              caption: i18n.t('common.birthInfo'),
              colCount: 2,
              items: [
                {
                  dataField: 'customer.birth_country',
                  label: { text: i18n.t('common.birthCountry') },
                  editorOptions: { validationTabIndex: 1 },
                },
                { dataField: 'customer.birth_place', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.birth_city', editorOptions: { validationTabIndex: 1 }, colSpan: 2 },
                { dataField: 'customer.birth_date', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.job_type_ref', editorOptions: { validationTabIndex: 1 } }
              ],
            },
            {
              itemType: 'group',
              name: 'identityDataGroup',
              caption: i18n.t('common.identityData'),
              colSpan: 2,
              colCount: 2,
              items: [
                { dataField: 'customer.identity_document_number', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.identity_document_type_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.identity_document_authority_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.identity_document_date', editorOptions: { max: new Date(), validationTabIndex: 1 } },
                { dataField: 'customer.identity_document_city', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.identity_document_state', editorOptions: { validationTabIndex: 1 } }
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.additionalInfo', 2),
              colSpan: 2,
              colCount: 4,
              items: [
                { dataField: 'customer.buyer_type_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.channel_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.channel_type_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.customer_type_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.discount_list_ref', editorOptions: { validationTabIndex: 1 } },
                {
                  itemType: 'formItem',
                  editorType: 'dxTextArea',
                  dataField: 'customer.social_link',
                  editorOptions: { autoResizeEnabled: true, maxHeight: '4rem', validationTabIndex: 1 },
                  colSpan: 3
                },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.privacyConsent', 2),
              colSpan: 2,
              colCount: 2,
              items: [
                { dataField: 'customer.marketing_communications_consent', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.third_party_data_transfer_consent', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'customer.privacy_check', editorOptions: { validationTabIndex: 1 } },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.customerAccounting'),
          colCount: 2,
          tabIndex: 2,
          tabTemplate: 'validationTabTemplate',
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.mainData'),
              colSpan: 2,
              colCount: 4,
              items: [
                { dataField: 'customer_accounting.customer_accounting_status_type', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.vat_type_ref', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.payment_type_ref', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.currency_ref', editorOptions: { validationTabIndex: 2 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.grouping', 2),
              colSpan: 2,
              colCount: 4,
              items: [
                { dataField: 'customer_accounting.single_invoice', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.riba_grouping', editorOptions: { validationTabIndex: 2 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.expense', 2),
              colSpan: 2,
              colCount: 4,
              items: [
                { dataField: 'customer_accounting.is_stamp_duty_customer', editorOptions: { validationTabIndex: 2 }},
                { dataField: 'customer_accounting.remind_packaging', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.remind_insurance', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.remind_payment_collection', editorOptions: { validationTabIndex: 2 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.bank'),
              colSpan: 2,
              colCount: 4,
              items: [
                { dataField: 'customer_accounting.iban', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.swift_code', editorOptions: { validationTabIndex: 2 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.billing'),
              colSpan: 2,
              colCount: 4,
              items: [
                { dataField: 'customer_accounting.invoice_to_customer', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.master_account_code', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.accounting_code', editorOptions: { validationTabIndex: 2 } },
                { dataField: 'customer_accounting.do_not_export', editorOptions: { validationTabIndex: 2 } },
              ]
            }
          ]
        },
        {
          title: i18n.t('common.electronicInvoice'),
          colCount: 2,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 3,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.electronicInvoiceData'),
              colCount: 2,
              items: [
                { dataField: 'customer_electronic_invoice.agreement_type_ref', editorOptions: { validationTabIndex: 3 } },
                { dataField: 'customer_electronic_invoice.agreement_type_code', editorOptions: { validationTabIndex: 3 } },
                { dataField: 'customer_electronic_invoice.email_certified', editorOptions: { validationTabIndex: 3 } },
                { dataField: 'customer_electronic_invoice.verify_email_certified', editorOptions: { validationTabIndex: 3 } },
                { dataField: 'customer_electronic_invoice.sdi_code', editorOptions: { validationTabIndex: 3 } },
                { dataField: 'customer_electronic_invoice.administrative_reference', editorOptions: { validationTabIndex: 3 } },
                { dataField: 'customer_electronic_invoice.split_payment', editorOptions: { validationTabIndex: 3 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.contactInfo'),
              colCount: 2,
              items: [
              { dataField: 'customer_electronic_invoice.protocol', editorOptions: { validationTabIndex: 3 } },
              { dataField: 'customer_electronic_invoice.serial_number', editorOptions: { validationTabIndex: 3 } },
              { dataField: 'customer_electronic_invoice.declaration_date', editorOptions: { validationTabIndex: 3 } },
              { dataField: 'customer_electronic_invoice.expiration_date', editorOptions: { validationTabIndex: 3 } },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.supplier'),
          colCount: 2,
          tabTemplate: 'validationTabTemplate',
          tabIndex: 4,
          visible: false,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.mainInfo'),
              colSpan: 2,
              colCount: 2,
              items: [
                { dataField: 'supplier.supplier_type_ref', editorOptions: { validationTabIndex: 4 } },
                { dataField: 'supplier.supplier_brand_ref', editorOptions: { validationTabIndex: 4 } },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.supplierAccounting'),
          colCount: 2,
          tabIndex: 5,
          visible: false,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.mainData'),
              colSpan: 2,
              colCount: 2,
              items: [
                { dataField: 'supplier_accounting.trade_account_ref', editorOptions: { showClearButton: true, validationTabIndex: 5 } },
                { dataField: 'supplier_accounting.financial_center_ref', editorOptions: { showClearButton: true, validationTabIndex: 5 } },
                { dataField: 'supplier_accounting.master_account_code', editorOptions: { validationTabIndex: 5 } },
                { dataField: 'supplier_accounting.accounting_code', editorOptions: { validationTabIndex: 5 } },
                { dataField: 'supplier_accounting.do_not_export', editorOptions: { validationTabIndex: 5 } },
              ]
            },
          ]
        }
      ]
    }
  ],
  onInitialized: (e) => {
    formInstance.value = e.component
    currentEditingTabIndex.value = 0
    e.component.option('readOnly', isViewOnly.value)
  },
  customizeItem: customizeItem,
}, {
  onInitializedCustom: (e) => onInitializedCustom(e),
  entityLock: entityLock,
})

const resetInputFields = () => {
  currentCountryId.value =
  currentStateId.value =
  currentSubjectTypeId.value =
  currentSubjectSubTypeId.value =
  currentVatCountryId.value =
  currentCertifiedEmail.value =
  currentVerifyCertifiedEmail.value =
  currentSdiCode.value = null
  currentTradeAccountId.value =
  currentFinancialCenterId.value = null
  currentBirthStateId.value =
  currentBirthCityId.value = null
  resetTaxCodeData()
}

const syncTaxCodeData = (data) => {
  calculatedTaxCodeData.value = {
    name: data?.name ?? null,
    nameExt: data?.name_ext ?? null,
    birthDate: data?.customer?.birth_date ?? null,
    birthCountryId: data?.customer?.birth_country ?? null,
    birthPlace: data?.customer?.birth_place ?? null,
    birthCity: data?.customer?.birth_city ?? null,
    taxCode: data?.tax_code ?? null,
  }
}

const onInitNewRow = (e) => {
  isViewOnly.value = false
  validationTabIndexErrors.value = []
  resetInputFields()
  if (registryStore.companyData.default_vat_country) {
    e.data.vat_country_id = currentVatCountryId.value = registryStore.companyData.default_vat_country
  }

  currentSubjectTypeId.value = null
  e.data.type = registryStore.subjectEntityTypes.CUSTOMER

  setDefaultCustomerData(e.data)
  e.promise = registryStore.api.getSubjectNextIdAsync().then(res => { e.data.code = res.data })
}

let firstEditLoad = true

const onEditorPreparing = async(e) => {
  const defaultValueChanged = e.editorOptions.onValueChanged

  if (e.parentType === 'dataRow' && isViewOnly.value) {
    e.editorOptions.readOnly = true
  }

  registerLabelEditors(e, editors, subjectLabelFields)

  if (e.parentType === 'dataRow' && e.dataField === 'subject_type_ref') {
    currentSubjectTypeId.value = e.row?.data?.subject_type_ref
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      currentSubjectTypeId.value = args.value
      currentSubjectSubTypeId.value = null
      editors['customer_electronic_invoice.sdi_code']?.option('maxLength', getSdiCodeMaxLength())
      disableFields([birthInfoGroup.value, identityDataGroup.value, vatNumberCell.value, vatCountryCell.value, taxCodeCell.value])
      updateSubjectLabels()
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'subject_sub_type_ref') {
    currentSubjectSubTypeId.value = e.row?.data?.subject_sub_type_ref
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      currentSubjectSubTypeId.value = args.value
      editors['customer_electronic_invoice.sdi_code']?.option('maxLength', getSdiCodeMaxLength())
      disableFields([birthInfoGroup.value, identityDataGroup.value, vatNumberCell.value, vatCountryCell.value, taxCodeCell.value])
      updateSubjectLabels()
    }
  }
  const taxCodeFields = { 'name': 'name', 'name_ext': 'nameExt', 'tax_code': 'taxCode', 'customer.birth_date': 'birthDate' }
  if (e.parentType === 'dataRow' && taxCodeFields[e.dataField]) {
    calculatedTaxCodeData.value[taxCodeFields[e.dataField]] = e.value ?? null
  }
  if (e.parentType === 'dataRow' && ['name', 'name_ext', 'tax_code'].includes(e.dataField)) {
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      calculatedTaxCodeData.value[taxCodeFields[e.dataField]] = args.value
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'birth_state') {
    e.editorOptions.disabled = !hasIdentityData(e.row?.data?.subject_type_ref)
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vat_number') {
    e.editorOptions.disabled = !isVatCountryIta(e.row?.data?.vat_country_id)
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vat_country_id') {
    e.editorOptions.onInitialized = (e) => { editors['vat_country_id'] = e.component }
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'customer.birth_date' || e.dataField === 'customer.identity_document_date' || e.dataField === 'customer_electronic_invoice.declaration_date' || e.dataField === 'customer_electronic_invoice.expiration_date')) {
    e.editorOptions.onValueChanged = (args) => {
      args.value = args.value && moment(args.value).format('YYYY-MM-DD')
      defaultValueChanged(args)
      if (e.dataField === 'customer.birth_date') {
        calculatedTaxCodeData.value.birthDate = args.value
      }
    }
  }
  if (firstEditLoad && e.dataField && e.row?.data?.id && !isViewOnly.value) {
    firstEditLoad = false
    disableFields([birthInfoGroup.value, identityDataGroup.value, vatNumberCell.value, vatCountryCell.value, taxCodeCell.value])
    e.promise = registryStore.api.getSubjectDetailsAsync(e.row?.data?.id).then(res => {
      e.row.data = res.data
      currentCountryId.value = e.row.data.address?.country
      currentStateId.value = e.row.data.address?.state
      syncTaxCodeData(e.row.data)
      formInstance.value?.repaint()
      dataGrid.openEditingTab.value = null
    })
  }

  if (e.parentType === 'dataRow' && e.dataField === 'customer_electronic_invoice.email_certified') {
    e.editorOptions.onInitialized = (e) => { editors['customer_electronic_invoice.email_certified'] = e.component }
    e.editorOptions.disabled = e.row?.data?.customer_electronic_invoice?.sdi_code

    currentCertifiedEmail.value = e.row?.data?.customer_electronic_invoice?.email_certified
    if (currentCertifiedEmail.value) {
      e.row.data.customer_electronic_invoice.verify_email_certified = currentCertifiedEmail.value
    }
    currentVerifyCertifiedEmail.value = currentCertifiedEmail.value

    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      currentCertifiedEmail.value = args.value
      e.component.cellValue(e.row.rowIndex, 'customer_electronic_invoice.verify_email_certified', currentVerifyCertifiedEmail.value)
      editors['customer_electronic_invoice.sdi_code'].option('disabled', args.value)
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer_electronic_invoice.verify_email_certified') {
    e.editorOptions.onInitialized = (e) => { editors['customer_electronic_invoice.verify_email_certified'] = e.component }
    e.editorOptions.disabled = e.row?.data?.customer_electronic_invoice?.sdi_code

    currentVerifyCertifiedEmail.value = e.row?.data?.customer_electronic_invoice?.verify_email_certified
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      currentVerifyCertifiedEmail.value = args.value
      e.component.cellValue(e.row.rowIndex, 'customer_electronic_invoice.email_certified', currentCertifiedEmail.value)
      editors['customer_electronic_invoice.sdi_code'].option('disabled', args.value)
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'customer_electronic_invoice.sdi_code') {
    e.editorOptions.onInitialized = (e) => { editors['customer_electronic_invoice.sdi_code'] = e.component }
    e.editorOptions.maxLength = getSdiCodeMaxLength()
    e.editorOptions.disabled = e.row?.data?.customer_electronic_invoice?.email_certified
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      editors['customer_electronic_invoice.email_certified'].option('disabled', args.value)
      editors['customer_electronic_invoice.verify_email_certified'].option('disabled', args.value)
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'supplier_accounting.trade_account_ref') {
    currentTradeAccountId.value = e.row?.data?.supplier_accounting?.trade_account_ref
    e.editorOptions.onInitialized = (e) => { editors['trade_account_ref'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      editors['financial_center_ref'].option('dataSource', getFilteredFinancialCenters())
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'supplier_accounting.financial_center_ref') {
    currentFinancialCenterId.value = e.row?.data?.supplier_accounting?.financial_center_ref
    e.editorOptions.onInitialized = (e) => { editors['financial_center_ref'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      editors['trade_account_ref'].option('dataSource', getFilteredTradeAccounts())
    }
  }
}

const onEditingStart = (e) => {
  firstEditLoad = true
  isViewOnly.value = e.data?.id > 0
  validationTabIndexErrors.value = []
  resetInputFields()
  syncTaxCodeData(e.data)

  if (e.data?.address?.country) {
    currentCountryId.value = e.data.address.country
  }
  if (e.data?.address?.state) {
    currentStateId.value = e.data.address.state
  }
  if (e.data?.subject_type_ref) {
    currentSubjectTypeId.value = e.data.subject_type_ref
  }
  if (e.data?.subject_sub_type_ref) {
    currentSubjectSubTypeId.value = e.data.subject_sub_type_ref
    currentSubjectTypeId.value = currentSubjectType.value?.parent_ref
  }
  if (e.data?.vat_country_id) {
    currentVatCountryId.value = e.data.vat_country_id
  }
  if (e.data?.customer_electronic_invoice?.email_certified) {
    currentCertifiedEmail.value = e.data.customer_electronic_invoice.email_certified
  }
  if (e.data?.customer_electronic_invoice?.sdi_code) {
    currentSdiCode.value = e.data.customer_electronic_invoice.sdi_code
  }
  if (e.data?.supplier_accounting?.financial_center_ref) {
    currentFinancialCenterId.value = e.data.supplier_accounting.financial_center_ref
  }
  if (e.data?.supplier_accounting?.trade_account_ref) {
    currentTradeAccountId.value = e.data.supplier_accounting.trade_account_ref
  }

  if (e.data?.type) {
    currentSubjectEntityType.value = e.data.type
  }
}

const onTabPanelSelectionChanged = () => { gridRef.value.instance.updateDimensions() }

const { validationTabIndexErrors, onTabsValidating } = useValidationTabs()

const onRowValidating = (e) => {
  validationTabIndexErrors.value = []
  onTabsValidating(e)
  if (e.newData?.customer?.privacy_check && !registryStore.companyData.data_controller_name) {
    e.isValid = false
    validationTabIndexErrors.value.push(1)
    notifyError(i18n.t('common.privacyNoteIncomplete'), {
      actions: [
        {
          label: i18n.t('common.complete_action'), size: 'sm', color: 'white', handler: async() => {
            const route = { path: menuRoutesEnum.COMPANY, query: { tab: 9 } }
            await router.push(route)
          }
        }
      ]
    })
  }
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  const allHaveCustomer = e.selectedRowsData.every(row => row.customer_id != null)
  print.printToolbarButton.value.option('disabled', !allHaveCustomer)
}

const onSaved = async(e) => {
  clearSubjectValidation()
  await Promise.all([registryStore.fetchCustomersAsync(true), registryStore.fetchSuppliersAsync(true)])
}

const onEditCanceled = () => clearSubjectValidation()

const onClearFilter = () => { router.replace() }

const onRowUpdating = (e) => {
  if (e.key) {
    e.newData.type = currentSubjectEntityType.value
  }
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    print.printToolbarWidget({ onClick: async() => {
      try {
        const filename = selectedRows.value.length <= 1 ? 'privacy.pdf' : 'privacy.zip'
        await registryStore.api.printPrivacyAsync(filename, {filename: filename, ids: selectedRows.value.map(i => i.customer_id), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}, { skipErrorMessage: (e) => true })

        if (!registryStore.companyData.print_privacy_template_adopted) {
          const confirmed = await customDialog(
            i18n.t('common.privacy'),
            i18n.t('common.privacyModuleApproved'),
            i18n.t('common.yes'),
            i18n.t('common.no')
          )

          if (confirmed) {
            const companyData = { id: registryStore.companyData.id, print_privacy_template_adopted: true }
            await registryStore.api.updateCompanyDataAsync(companyData)
            await registryStore.fetchCompanyDataAsync(true)
          }
        }
      } catch (err) {
        notifyError(i18n.t(err?.response?.data?.message || 'error.generic'))
      }
    },
    disabled: false,
    }),
  )

  dataGrid.useDefaultToolbar(e, {
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.subject')}` },
  })

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.subjectsUrl),
  columns: columns,
  editing: editing,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  selection: dataGrid.useDefaultGridSelection(),
  onEditorPreparing: onEditorPreparing,
  onEditingStart: onEditingStart,
  onInitNewRow: onInitNewRow,
  onRowUpdating: onRowUpdating,
  onRowValidating: (e) => onRowValidating(e, gridRef),
  onSaved: onSaved,
  onEditCanceled: onEditCanceled,
  onClearFilter: onClearFilter,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onContentReady: onGridContentReady,
  routerFilter: {
    fields: [{code: 'code'}],
    getRoute: () => route,
    router: router
  }
})

const updateSubjectLabels = () => updateEditorLabels(editors, subjectLabelFields, buildItemLabel)

const onCountryChanged = (e, dataField) => {
  const newCountryId = e?.value?.id ?? null
  if (dataField === 'vat_country_id') {
    currentVatCountryId.value = newCountryId
    editors['vat_number']?.option('value', null)
    updateSubjectLabels()
  } else if (dataField === 'customer.birth_country') {
    calculatedTaxCodeData.value.birthCountryId = newCountryId
    calculatedTaxCodeData.value.birthPlace = null
    calculatedTaxCodeData.value.birthCity = null
    currentBirthStateId.value = null
    currentBirthCityId.value = null
  } else {
    currentCountryId.value = newCountryId
    currentZipCode.value = null
  }
}

const onBirthStateChanged = (e) => {
  const value = e?.value
  const isStateRecord = value && typeof value === 'object'
  currentBirthStateId.value = isStateRecord ? value.id ?? null : null
  currentBirthCityId.value = null
  calculatedTaxCodeData.value.birthPlace = isStateRecord ? value.code ?? null : value || null
  calculatedTaxCodeData.value.birthCity = null
}

const onBirthCityChanged = (e) => {
  const value = e?.value
  if (value && typeof value === 'object') {
    currentBirthCityId.value = value.id ?? null
    calculatedTaxCodeData.value.birthCity = value.name ?? null
    const birthState = webSiteStore.states.find(s => s.id === value.state)
    if (birthState) {
      currentBirthStateId.value = birthState.id
      calculatedTaxCodeData.value.birthPlace = birthState.code
    }
  } else {
    currentBirthCityId.value = null
    calculatedTaxCodeData.value.birthCity = value || null
  }
}

const onStateChanged = (e) => {
  currentStateId.value = e?.value?.id ?? null
  currentZipCode.value = null
}

const isTransmissionTabVisible = (data) => { return isSupplier(data) && data.id > 0 }

const onTabPanelContentReady = (e) => {
  const firstVisibleTabIndex = e.component.option('items').findIndex(item => item.visible !== false)
  if (firstVisibleTabIndex >= 0) {
    e.component.option('selectedIndex', firstVisibleTabIndex)
  }
}

const privacyCheckItems = [
  {
    model: 'customer_third_party_data_transfer_consent',
    label: 'common.thirdPartyDataTransferConsent'
  },
  {
    model: 'customer_marketing_communications_consent',
    label: 'common.marketingCommunicationsConsent'
  }
]
const getPrivacyCheckColor = (data) => {
  if (data.customer_privacy_check && data.customer_third_party_data_transfer_consent && data.customer_marketing_communications_consent) {
    return { color: 'green', showTooltip: false }
  }
  if (data.customer_privacy_check && !(data.customer_third_party_data_transfer_consent && data.customer_marketing_communications_consent)) {
    return { color: 'orange', showTooltip: true }
  }
  return { color: 'red', showTooltip: false }
}

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #attachmentsCellTemplate="{ data: cellInfo }">
        <attachment-icon-list
          :data="cellInfo.data"
          document-name="common.subject"
          :has-attachments="cellInfo.data?.has_attachments"
          :document-field="'attachment_subject_ref'"
          :form-data-source="{
            attachment_category: portalStore.attachmentCategoryEnum.SUBJECT,
          }"
          :entities-id="getAttachmentEntities(cellInfo.data, ['subject'])"
          :refresh-grid-async="() => refreshGridAsync()"
        />
      </template>
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template #generic-caption-template="{ data: cellInfo }">
        <div class="dx-form-group-caption-wrapper">
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>
      <template #main-info-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="cellInfo.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ cellInfo.caption }}
            </span>
          </div>
          <div v-if="!isViewOnly">
            <q-btn
              icon="mdi-card-account-details-outline"
              flat
              size="xs"
              round
              color="light-blue"
              :disable="isTaxCodeCalculationDisabled"
              @click="onCalculateTaxCode"
            >
              <q-tooltip>
                {{ i18n.t('common.calculateTaxCode') }}
              </q-tooltip>
            </q-btn>
          </div>
        </div>
      </template>
      <template #validationTabTemplate="{data: templateOptions}">
        <ValidationTab
          :title="templateOptions.title"
          :has-error="validationTabIndexErrors.includes(templateOptions.tabIndex)"
        />
      </template>
      <template #master-detail="{ data: templateOptions }">
        <dx-tab-panel
          animation-enabled
          @selection-changed="onTabPanelSelectionChanged"
          @content-ready="onTabPanelContentReady"
        >
          <dx-item
            :title="$t('common.contact', 2)"
            template="contacts-tab"
          />
          <template #contacts-tab>
            <subject-contact-grid
              :contact-type="isCustomer(templateOptions.data) ? registryStore.contactTypes.CUSTOMER : registryStore.contactTypes.SUPPLIER"
              :subject="templateOptions.data"
              :entity-lock="entityLock"
            />
          </template>
          <dx-item
            :visible="isSupplier(templateOptions.data)"
            :title="$t('common.division', 2)"
            template="division-tab"
          />
          <template #division-tab>
            <supplier-division-grid
              v-if="isSupplier(templateOptions.data)"
              :subject="templateOptions.data"
              :entity-lock="entityLock"
            />
          </template>
          <dx-item
            :title="$t('common.subjectDocument', 2)"
            template="documents-tab"
          />
          <template #documents-tab>
            <subject-document-grid
              :subject="templateOptions.data"
            />
          </template>
          <dx-item
            :visible="isCustomer(templateOptions.data)"
            :title="$t('common.profile', 2)"
            template="profile-tab"
          />
          <template #profile-tab>
            <subject-profile-grid
              :subject="templateOptions.data"
              :entity-lock="entityLock"
            />
          </template>
          <dx-item
            :visible="isSupplier(templateOptions.data)"
            :title="$t('common.subAgency', 2)"
            template="sub-agency-tab"
          />
          <template #sub-agency-tab>
            <subject-profile-grid
              subagency
              :subject="templateOptions.data"
              :entity-lock="entityLock"
            />
          </template>
          <dx-item
            :visible="isCustomer(templateOptions.data)"
            :title="$t('common.privacy')"
            template="privacy-tab"
          />
          <template #privacy-tab>
            <customer-privacy-module-grid
              :subject="templateOptions.data"
              :entity-lock="entityLock"
              :customer-id="templateOptions.data.customer_id"
              :privacy-module-type="registryStore.privacyModuleTypes.CUSTOMER"
            />
          </template>
          <dx-item
            :visible="isTransmissionTabVisible(templateOptions.data)"
            :title="$t('common.transmission', 2)"
            template="transmission-tab"
          />
          <template #transmission-tab>
            <supplier-brand-transmission-grid
              :supplier-brand-id="templateOptions.data.supplier_brand_ref"
            />
          </template>
        </dx-tab-panel>
      </template>
      <template #typeCellTemplate="{ data: templateOptions }">
        <q-chip
          v-if="!!templateOptions.data.type"
          size="sm"
          text-color="white"
          style="margin: 0"
          :label="registryStore.getLocalizedSubjectEntityTypes().find(x => x.id === templateOptions.data.type)?.name"
          :color="registryStore.getLocalizedSubjectEntityTypes().find(x => x.id === templateOptions.data.type)?.color"
        />
      </template>
      <template #isPartialCellTemplate="{ data: templateOptions }">
        <q-chip
          size="sm"
          text-color="white"
          style="margin: 0"
          :label="templateOptions.data.is_partial ? i18n.t('common.partial') : i18n.t('common.complete')"
          :color="templateOptions.data.is_partial ? 'orange' : 'grey'"
        />
      </template>
      <template #privacyCheckCellTemplate="{ data: templateOptions }">
        <q-chip
          v-if="!isNullOrUndefined(templateOptions.data.customer_privacy_check)"
          dense
          square
          size="sm"
          text-color="white"
          style="margin: 0"
          :color="getPrivacyCheckColor(templateOptions.data).color"
        >
          <q-icon
            name="mdi-check-all"
            size="12px"
          />
          <q-tooltip
            v-if="getPrivacyCheckColor(templateOptions.data).showTooltip"
            class="bg-grey-9 text-white shadow-2"
            anchor="top middle"
            self="bottom middle"
          >
            <div class="column q-gutter-xs q-pa-xs">
              <q-checkbox
                v-for="item in privacyCheckItems"
                :key="item.model"
                v-model="templateOptions.data[item.model]"
                :label="i18n.t(item.label)"
                dense
                disable
                dark
                size="sm"
              />
            </div>
          </q-tooltip>
        </q-chip>
      </template>
      <template #countryEditor="{ data: cellInfo }">
        <country-drop-down-box
          :data="cellInfo"
          :is-portal="['customer.birth_country', 'vat_country_id'].includes(cellInfo.column?.dataField)"
          :disabled="isViewOnly || (['customer.birth_country'].includes(cellInfo.column?.dataField) && cellInfo.column?.editorOptions.disabled) || (!!cellInfo.data.id && !entityLock.isLocked(true, null, true))"
          @value-changed="onCountryChanged($event, cellInfo.column?.dataField)"
        />
      </template>
      <template #birthCountryEditor="{ data: cellInfo }">
        <country-drop-down-box
          :data="cellInfo"
          :is-portal="['customer.birth_country', 'vat_country_id'].includes(cellInfo.column?.dataField)"
          :disabled="isViewOnly || (['customer.birth_country'].includes(cellInfo.column?.dataField) && cellInfo.column?.editorOptions.disabled) || (!!cellInfo.data.id && !entityLock.isLocked(true, null, true))"
          @value-changed="onCountryChanged($event, cellInfo.column?.dataField)"
        />
      </template>
      <template #cityEditor="{ data: cellInfo }">
        <city-drop-down-box
          :data="cellInfo"
          :value="cellInfo.data.address?.city"
          :value-text="cellInfo.data.address?.city_text"
          :country="currentCountryId"
          :state="currentStateId"
          :zip-code="currentZipCode"
          :filtered-cities-count="currentFilteredCitiesCount"
          :disabled="isViewOnly || (!!cellInfo.data.id && !entityLock.isLocked(true, null, true))"
        />
      </template>
      <template #stateEditor="{ data: cellInfo }">
        <div>
          <state-drop-down-box
            :data="cellInfo"
            :value="cellInfo.data.address?.state"
            :value-text="cellInfo.data.address?.state_text"
            :country="currentCountryId"
            :disabled="isViewOnly || (!!cellInfo.data.id && !entityLock.isLocked(true, null, true))"
            @value-changed="onStateChanged($event)"
          />
        </div>
      </template>
      <template #birthStateEditor="{ data: cellInfo }">
        <state-drop-down-box
          :data="cellInfo"
          :value="currentBirthStateId"
          :value-text="cellInfo.data.customer?.birth_place"
          :country="calculatedTaxCodeData.birthCountryId"
          :disabled="isViewOnly || !!cellInfo.column?.editorOptions?.disabled || (!!cellInfo.data.id && !entityLock.isLocked(true, null, true))"
          @value-changed="onBirthStateChanged($event)"
        />
      </template>
      <template #birthCityEditor="{ data: cellInfo }">
        <city-drop-down-box
          :data="cellInfo"
          :value="currentBirthCityId"
          :value-text="currentBirthCityId ? null : cellInfo.data.customer?.birth_city"
          :country="calculatedTaxCodeData.birthCountryId"
          :state="currentBirthStateId"
          :disabled="isViewOnly || !!cellInfo.column?.editorOptions?.disabled || (!!cellInfo.data.id && !entityLock.isLocked(true, null, true))"
          @value-changed="onBirthCityChanged($event)"
        />
      </template>
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          :data="cellInfo"
          show-clear-button
          :exclude="cellInfo.data?.customer_id"
          :disabled="isViewOnly"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Subject'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
        />
      </template>
    </dx-data-grid>
  </div>
</template>

<style scoped lang="scss">
.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
